import { Container } from "@ubnt/store.core/lib/components/Container";
import * as S from "./CaseStudyBlock.styles";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { VideoModal } from "pages/LayoutComponents/VideoModal";
import { useTranslation } from "hooks/useTranslation";
import CaseStudyFedexForumVideo from "./assets/case-study-fedex-forum.mp4";
import { useInitialAutoscrollToFragment } from "hooks/useInitialAutoscrollToFragment";
import { HEADER_NAVIGATION_CAROUSEL_STICKY_TRANSITION_OFFSET } from "@ubnt/store.core/lib/components/Header/Navigation/Carousel/Carousel.styles";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";

import fedexForumMp4 from "./assets/fedex-forum.mp4";
import fedexForumPosterJpg from "./assets/fedex-forum-poster.jpg";
import fedexSvg from "./assets/fedex.svg";
import grizzliesSvg from "./assets/grizzlies.svg";
import caseStudyJpg from "./assets/case-study.jpg";
import playSvg from "../../../../../../assets/play.svg";

// Requested by Andre for direct linking
const FRAGMENT_ID = "case-study";

export const CaseStudyBlock = ({
  isAlignLeft,
  isInView,
}: {
  isAlignLeft?: boolean;
  isInView?: boolean;
}) => {
  const { t } = useTranslation();

  const videoRef = useRef(null);

  const [videoModalOpen, setVideoModalOpen] = useState(false);

  useInitialAutoscrollToFragment({
    delay: 200,
    offset: HEADER_NAVIGATION_CAROUSEL_STICKY_TRANSITION_OFFSET,
  });

  const playVideo = useCallback(async (videoRef) => {
    try {
      await videoRef.current?.play();
    } catch (error) {
      console.error("Error during video playback:", error);
    }
  }, []);

  const pauseVideo = useCallback(async (videoRef) => {
    try {
      await videoRef.current?.pause();
    } catch (error) {
      console.error("Error when pausing video:", error);
    }
  }, []);

  useEffect(() => {
    if (isInView) {
      playVideo(videoRef);
    }

    return () => {
      pauseVideo(videoRef);
    };
  }, [isInView, playVideo, pauseVideo]);

  return (
    <S.WrapContainer>
      <S.Wrap id={FRAGMENT_ID}>
        <S.StyledVideo
          ref={videoRef}
          src={fedexForumMp4}
          poster={getOptimizedImageUrl(
            absoluteAssetUrl(fedexForumPosterJpg),
            1920 * 2,
          )}
          loop
          muted
          playsInline
        />

        <Container size="desktop" noVerticalMargin>
          <S.InnerWrap>
            <S.Title>
              {t("cloudGateways.enterpriseScale.caseStudy.title")}
            </S.Title>
            <S.Subtitle>
              {t("cloudGateways.enterpriseScale.caseStudy.subtitle")}
            </S.Subtitle>

            <S.LogoWrap>
              <S.Logo src={fedexSvg} alt="FedEx" />
              <S.And />
              <S.Logo src={grizzliesSvg} alt="Memphis Grizzlies" />
            </S.LogoWrap>
            <S.WatchVideoButton
              variant="secondary"
              $isAlignLeft={isAlignLeft}
              onClick={() => setVideoModalOpen(true)}
            >
              <S.PlayIcon src={playSvg} width={20} height={18} />
              <span>{t("cloudGateways.enterpriseScale.caseStudy.cta")}</span>
            </S.WatchVideoButton>
          </S.InnerWrap>
        </Container>
      </S.Wrap>

      <VideoModal
        src={{
          hd: CaseStudyFedexForumVideo,
        }}
        poster={getOptimizedImageUrl(absoluteAssetUrl(caseStudyJpg), 1920 * 2)}
        isOpen={videoModalOpen}
        motif="light"
        onClose={() => setVideoModalOpen(false)}
      />
    </S.WrapContainer>
  );
};
